import {match} from 'ts-pattern';

import {memo, useMemo} from 'react';

import {isNotNil} from 'ramda-adjunct';

import {ROW_BORDER_BOTTOM} from '../../../../constants/borderStyles';
import {
  selectHorizontalSeparatorsVisible,
  selectVerticalSeparatorsVisible,
} from '../../../../store/createEagGridStore';
import {useActionsDefinition} from '../../../../store/selectors/useActionsDefinition';
import {useGridSettingsSafe} from '../../../../store/selectors/useGridSettingsSafe';
import {useEagGridSelector} from '../../../../store/useEagGridSelector';
import type {EagColumn} from '../../../../types/EagColumn';
import type {EagColumnState} from '../../../../types/EagColumnState';
import type {RowData} from '../../../../types/RowData';
import {getPinnedEdgeShadowClass} from '../../../../utils/getPinnedEdgeShadowClass';
import {LineHeightToPx} from '../../../../utils/lineHeightToValue';
import {GridCell} from './components/GridCell/GridCell';
import {RowActions} from './components/RowActions/RowActions';
import {RowSelectCheckbox} from './components/RowSelectCheckbox/RowSelectCheckbox';

// GridRow reads grid settings and separator visibility from the store once,
// then passes verticalSeparators and valueWrapMode to GridCell as props.
// This avoids per-cell store subscriptions for rarely-changing settings —
// 1 subscription per row instead of 1 per cell.

export type GridRowProps = {
  columns: EagColumn[];
  columnStates: EagColumnState[];
  row: RowData;
  isSelected: boolean;
  onRowClick?: (row: RowData, event: React.MouseEvent) => void;
  pinnedLeftOffsets: Map<string, number>;
  pinnedRightOffsets: Map<string, number>;
  isStriped?: boolean;
  rowIndex: number;
  lastPinnedLeftKey: string | null;
  firstPinnedRightKey: string | null;
  // Action column props — only present when action column is enabled
  onActionClick?: (actionKey: string, row: RowData, event: React.MouseEvent) => void;
  actionColumnWidth?: number;
  isMobile?: boolean;
  disableAllActions?: boolean;
  // Selection props — controls whether the row renders a RowSelectCheckbox
  rowSelectMode: 'SINGLE' | 'MULTIPLE' | 'NONE';
  onToggleSelection?: (rowId: string) => void;
};

// memo: rendered N times in list; parent GridBody re-renders on loading/selection
// changes that don't affect most rows.
export const GridRow = memo((props: GridRowProps) => {
  const horizontalSeparators = useEagGridSelector(selectHorizontalSeparatorsVisible);
  const verticalSeparators = useEagGridSelector(selectVerticalSeparatorsVisible);
  const gridSettings = useGridSettingsSafe();
  const heightPx = LineHeightToPx[gridSettings.rowHeight];
  const actionsDefinition = useActionsDefinition();
  const valueWrapMode = gridSettings.valueWrapMode;

  /**
   * @mandatory O(1) column state Map used in renderCell() for every cell in this row.
   * Component is rendered N times; new Map() from array is not guaranteed referentially
   * stable by React Compiler. Unstable reference would cause downstream recomputation per cell.
   */
  const columnStateMap = useMemo(
    () => new Map(props.columnStates.map((cs) => [cs.key, cs])),
    [props.columnStates]
  );

  const nonRightPinnedColumns = props.columns.filter((col) => {
    const cs = columnStateMap.get(col.key);
    return cs?.pinned !== 'RIGHT';
  });

  const rightPinnedColumns = props.columns.filter((col) => {
    const cs = columnStateMap.get(col.key);
    return cs?.pinned === 'RIGHT';
  });

  const globalIndexMap = new Map(props.columns.map((col, i) => [col.key, i]));

  const handleClick = (event: React.MouseEvent) => {
    if (props.onRowClick) {
      props.onRowClick(props.row, event);
    }
  };

  const rowClassName = [
    'group/row',
    'hover:bg-slate-50',
    match({isSelected: props.isSelected, isStriped: props.isStriped})
      .with({isSelected: true}, () => 'bg-blue-50 hover:bg-blue-100')
      .with({isStriped: true}, () => 'bg-gray-50')
      .otherwise(() => ''),
    props.onRowClick ? 'cursor-pointer' : '',
  ]
    .filter(Boolean)
    .join(' ');

  function renderCell(column: EagColumn, globalIndex: number) {
    const columnState = columnStateMap.get(column.key);
    if (!columnState) {
      return null;
    }

    const isLastPinnedLeft = props.lastPinnedLeftKey === column.key;
    const isFirstPinnedRight = props.firstPinnedRightKey === column.key;
    const pinnedEdgeShadowClass = getPinnedEdgeShadowClass(
      columnState.pinned,
      isLastPinnedLeft || isFirstPinnedRight
    );
    const isLastColumn = globalIndex === props.columns.length - 1;
    const isLastBeforeActions = match({
      isLastColumn,
      hasActions: isNotNil(actionsDefinition),
    })
      .with({isLastColumn: true, hasActions: true}, () => true)
      .with({isLastColumn: true, hasActions: false}, () => true)
      .otherwise(() => false);

    return (
      <GridCell
        key={column.key}
        column={column}
        columnState={columnState}
        row={props.row}
        pinnedLeftOffset={props.pinnedLeftOffsets.get(column.key)}
        pinnedRightOffset={props.pinnedRightOffsets.get(column.key)}
        verticalSeparators={verticalSeparators}
        valueWrapMode={valueWrapMode}
        rowHeight={gridSettings.rowHeight}
        pinnedEdgeShadowClass={pinnedEdgeShadowClass}
        isLastBeforeActions={isLastBeforeActions}
      />
    );
  }

  return (
    <tr
      className={rowClassName}
      style={{height: heightPx, ...(horizontalSeparators ? ROW_BORDER_BOTTOM : undefined)}}
      onClick={handleClick}
      aria-rowindex={props.rowIndex}
    >
      {/* checkbox pseudo-column: rendered before data cells when selection is enabled */}
      {props.rowSelectMode !== 'NONE' && props.onToggleSelection && (
        <RowSelectCheckbox
          isSelected={props.isSelected}
          rowId={props.row.id}
          onToggleSelection={props.onToggleSelection}
          rowIndex={props.rowIndex}
        />
      )}
      {/* left-pinned + unpinned cells */}
      {nonRightPinnedColumns.map((column) =>
        renderCell(column, globalIndexMap.get(column.key) ?? 0)
      )}
      {/* filler td: absorbs remaining space between unpinned and right-pinned columns */}
      <td />
      {/* right-pinned cells */}
      {rightPinnedColumns.map((column) => renderCell(column, globalIndexMap.get(column.key) ?? 0))}
      {/* action column */}
      {actionsDefinition && props.onActionClick && props.actionColumnWidth ? (
        <RowActions
          row={props.row}
          actionsDefinition={actionsDefinition}
          onActionClick={props.onActionClick}
          isMobile={props.isMobile ?? false}
          rowHeight={gridSettings.rowHeight}
          actionColumnWidth={props.actionColumnWidth}
          heightPx={heightPx}
          isSelected={props.isSelected}
          isStriped={props.isStriped}
          isDisabled={props.disableAllActions}
        />
      ) : null}
    </tr>
  );
});
